// eslint-disable-next-line no-unused-vars
import React from 'react';
import { func, object, string, bool, number, array } from 'prop-types';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { Button, Table, Loading, TableCell, TableRow, TextInput } from '@carbon/react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import uniqueId from 'lodash/uniqueId';
import { AlertTriangle, Check, Package, X, Boxes, Plus, Trash } from 'lucide-react';

import Img from 'Containers/Image/Image';
import ProductPropType from 'Types/product';
import QuantitySelect from 'Containers/QuantitySelector/QuantitySelect';
import Price from 'Components/Price/Price';
import { renderStock } from 'Components/Products/helpers';
import QuantityBasedPrice from 'Components/QuantityBasedPrice/QuantityBasedPrice';
import CheckoutBadges from 'Components/Badges/CheckoutBadges';
import CartPromotionSuggestions from 'Containers/Products/CartPromotionSuggestions';
import { COUNTRIES, CURRENCY_RON } from 'Constants';
import useScreen from 'Hooks/useScreen';
import useRouter from 'Hooks/useRouter';

import useWheelHack from 'Hooks/useWheelHack';
import useNumberUtils from 'Hooks/useNumberUtils';

const StyledTableRow = styled(TableRow)`
  position: relative;
  height: 4.75rem !important;

  ${ props => {
    if (props.theme.updated || props.theme.new) {
      return 'top: -1px;';
    }
    return '';
  } }

  td {
    ${ props => props.theme.mainPromoItem ? 'padding-bottom: 15px !important' : '' };
  }

  &.promotionHeading {
    height: 40px !important;
    td {
      padding: 0 !important;
    }
  }

  &.promoItem {
    td {
      background-color: #FCFDFF;
      padding-top: 18px;
      padding-bottom: 18px;
      &.name {
        position: relative;
        &:before {
          display: block;
          position: absolute;
          left: 60px;
          top: -11px;
          content: ${ () => `url(${ Plus })` };
          color: ${ props => props.theme.products.colors.inStock };
          z-index: 13;
        }
      }
    }
    .quantitySelector {
      background-color: transparent;
    }
  }

  &.unReservable {
    .quantitySelector {
      background-color: transparent;
    }

    input:disabled {
      background: #fff;
    }
  }

  &.isUnreservable {
    height: auto !important;

    td {
      padding: 0 !important;

      > div {
        padding: 0 15px;
        justify-content: start;
        gap: 5px;
      }
    }
  }

  &.error {
    height: 10px !important;
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };
  }

  + .rowBorder {
    ${ props => props.theme.error && 'display: none;' };
    height: 1px;

    td {
      height: 1px;
      box-shadow: none;
      border: 0;
      background: transparent;

      hr {
        height: 1px;
        border: 0;
        margin: 0;
        width: 100%;
        background-color: ${ props => props.theme.products.colors.border };
      }
    }
  }

  td:first-of-type {
    padding-left: 15px;
  }

  td:last-of-type:not(.totalPriceNoVat) {
    padding-right: 15px;
  }

  td.name {
    width: ${ props => props.theme.ds ? '35%' : '40%' };
  }

  td.quantity {
    width: 15%;
  }

  td.unitPriceNoVat {
    width: ${ props => props.theme.ds ? '15%' : '20%' };
  }

  td.totalPriceNoVat {
    /* width: calc(15% - 55px); */
  }

  td.totalPriceWithVat {
    width: 15%;
  }

  td.deleteContainer {
    width: 55px;
  }

  .currentPriceContainer {
    width: 100%;
    justify-content: center;
  }


  &.delete {
    .overlay {
      &:after {
        background-color: rgba(0, 0, 0, 0.5) !important;
      }
    }

    .container {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      height: 100%;
      z-index: 5;
      margin-top: 50px;

      p {
        margin-bottom: 10px;
        font-weight: 600;
        color: ${ props => props.theme.colors.white };
      }

      button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid ${ props => props.theme.colors.default };
        margin: 0 10px;
        padding: 0;
        height: 30px;
        min-height: 0;
        width: 100px;

        &.cancel {
          color: ${ props => props.theme.colors.default };
        }
      }
    }
  }

  &.loading,
  &.delete {
    height: 1px !important;
    max-height: 1px;

    td {
      padding: 0 !important;
    }
  }

  &:hover {
    background-color: inherit !important;
  }

  td {
    padding: 10px 0;
    border: 0 none !important;
    font-size: 0.875rem;
    text-align: center;
    vertical-align: center;
    color: ${ props => props.theme.colors.darkGray };
    background-color: ${ props => {
    if (props.theme.error) {
      return props.theme.colors.lightRed;
    }
    if (props.theme.promoItem || props.theme.mainPromoItem) {
      return '#FCFDFF';
    }
    if (props.theme.isUnreservable) {
      return '#F4D9C0';
    }
    return props.theme.colors.white;
  } } !important;
  }

  p {
    font-size: 0.875rem;
    color: ${ props => props.theme.colors.darkGray };
  }

  a {
    display: flex;
    align-items: center;
    color: inherit;
    font-size: 0.875rem;
  }

  .code, .stock {
    color: ${ props => props.theme.colors.gray };
    font-size: 0.875rem;
  }

  .name {
    text-align: left;

    > div {
      position: relative;
      display: flex;
      align-items: center;
    }

    .badges {
      position: absolute;
      top: -2px;
      left: 5px;
    }

    .preview {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-right: 15px;
      width: 120px;
      height: 120px;
      padding: 10px;
      min-width: 120px;
      border: 1px solid ${ props => props.theme.products.colors.border };
      //background-color: ${ props => props.theme.error ? props.theme.colors.white : 'transparent' };
      background-color: ${ props => props.theme.colors.white };

      img {
        display: block;
        height: auto;
        max-height: 100%;
        max-width: 100%;
        width: auto;
      }

      .cds--loading svg {
        max-width: 100%;
      }

      svg:not(.cds--loading__svg) {
        position: relative;
        display: block;
        margin: 0 auto;
        height: auto;
        width: auto;
        max-height: 62%;
        max-width: 100%;
        stroke: #AFAEAE;
      }
    }

    .details {
      text-align: left;

      a {
        color: inherit;
      }

      .productAWBs {
        span {
          font-weight: 600;
        }

        button {
          cursor: pointer;
          background: transparent;
          border: none;
          color: #1F59A2;
        }
      }

      p {
        color: inherit;
        text-align: left;
        font-weight: 600;

        span {
          font-weight: normal;
        }
      }

      .title {
        &:hover {
          color: ${ props => props.theme.colors.blue };
        }

        margin-bottom: 5px;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-weight: normal;
        font-size: 0.875rem;
        line-height: 1.175rem;
        word-break: break-word;
      }
    }

    .quantityBasedPrices {
      text-align: left;
    }
  }

  .quantityBasedPrices {
    .qbpQuantity {
      display: inline-block;
      min-width: 50px;
    }
  }

  .stock,
  .price,
  .promotions,
  .quantity {
    color: ${ props => props.theme.colors.darkGray };
    text-align: center;
  }

  .stock {
    &.outOfStock {
      span {
        color: ${ props => props.theme.colors.default };
      }
    }

    span {
      margin-left: 5px;
    }
  }

  .quantity {
    .sufficient, .insufficient {
      p {
        font-weight: normal;
        margin-top: 5px;
        svg {
          margin-right: 3px;
          vertical-align: top;
        }
      }
    }

    .sufficient p {
      color: #169001;
    }

    .insufficient p {
      color: #BE2317;
    }

    p {
      font-size: 0.875rem !important;
      font-weight: 600;
      line-height: 1rem;
      margin-top: 15px;
    }

    input {
      height: 40px;
      font-size: 1rem;
      font-weight: normal;
      &:disabled {
        width: 50px;
      }
    }

    .mobileDetails {
      display: none;
    }
  }

  .totalPriceWithVat {
    > div > div {
      display: flex;
      align-items: center;
      justify-content: center;
        // justify-content: ${ props => props.theme.editable ? 'flex-end' : 'center' };
    }
  }

  .totalPriceWithVat .totalPrice {
    display: flex;
    justify-content: center;
    align-items: center;

    p {
      display: none;
    }

    > span {
      display: none;
    }

    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    input[type=number] {
      -moz-appearance: textfield;
    }

    .cds--form-item {
      max-width: 115px;
      min-width: 60px;
      width: 100%;
      margin-right: 10px;

      input {
        border: 1px solid ${ props => props.theme.products.colors.border };
        padding: 0 10px;
        box-sizing: border-box;
        color: ${ props => props.theme.colors.darkGray };
        font-size: 1.125rem;
        text-align: right;
      }
    }

    p {
      font-size: 1.125rem !important;
    }
  }

  .price {
    align-items: center;
    max-width: 70%;
    margin: 0 auto;

    .oldPrice {
      margin-left: 29px;
      font-size: 1rem;
      line-height: 1.375em;
      color: ${ props => props.theme.colors.gray };
      text-decoration: line-through;
    }

    .currentPrice {
      font-size: 1.125rem;
      line-height: 1.65rem;
      font-weight: 600;
      &:not(.free) {
        color: ${ props => props.theme.colors.darkGray };
      }
    }
  }

  .image {
    > * {
      margin: 0 auto;
    }
  }

  .quantitySelector {
    margin: 0 auto;
    background-color: ${ props => props.theme.error ? 'transparent' : props.theme.colors.white };
    ${ props => props.theme.error && `border-color: ${ props.theme.colors.default };` };
  }

  .delete {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    min-height: 0;
    padding: 0;
    border: 1px solid ${ props => props.theme.colors.pink };
    background-color: ${ props => props.theme.error ? props.theme.colors.white : props.theme.colors.lightPink };
    color: ${ props => props.theme.colors.darkGray };

    svg {
      transform: scale(1.1);
    }
  }

  .promoHeading {
    display: flex;
    align-items: center;
    position: relative;
    height: 40px;
    padding: 0 15px;
    border-top: 1px solid #B2C2D5;
    margin-top: -3px;
    background-color: #E7ECF5;
    font-size: 1rem;
    color: ${ props => props.theme.colors.darkGray };
    z-index: 50;

    svg {
      margin-right: 10px;
      color: inherit;
    }
  }

  .mobileDelete {
    display: none;
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    height: auto !important;
    display: flex;
    flex-direction: column;

    :not(.promoHeading) {
      border: 1px solid #E2E2E2 !important;
    }

    .promoHeading {
      height: 42px;
    }

    &:hover {
      background-color: transparent !important;
    }

    &.promotionHeading {
      display: table-row;
    }

    &.promoItem {
      padding-top: 10px;
      border-top: 1px solid #E2E2E2;

      td {
        padding-top: 10px;
        padding-bottom: 10px;

        &.name {
          &:before {
            left: calc(50% - 12px);
            top: -23px;
          }

          &:after {
            content: '';
            position: absolute;
            left: calc(50% - 30px);
            top: -12px;
            width: 60px;
            height: 3px;
            background: #fff;
          }
        }
      }
    }

    + .rowBorder {
      height: 15px;

      td {
        hr {
          display: none;
        }
      }
    }

    .name {
      text-align: left;

      .preview {
        width: 75px;
        height: 75px;
        min-width: 75px;
      }

      .details {
        .title {
          width: calc(100vw - 150px);
        }
      }
    }

    .mobileDelete {
      display: flex;
      justify-content: flex-end;
      padding-top: 5px;

      button {
        background: transparent;
        border: none;
        padding-right: 0;
        cursor: pointer;

        color: #D52B1E;

        svg {
          margin-right: 3px;
          fill: #D52B1E;
        }
      }
    }

    .quantity {
      display: flex;
      gap: 10px;
      align-items: center;
      justify-content: space-between;

      &.immutable {
        .quantitySelector {
          display: none;
        }
      }

      .mobileDetails {
        display: block;
      }

      .productAWBs {
        margin-top: 5px;

        span {
          font-weight: 600;
        }

        button {
          cursor: pointer;
          background: transparent;
          border: none;
          color: #1F59A2;
        }
      }

      p {
        margin-top: 5px;
        text-align: left;

        span {
          font-weight: normal;
        }
      }

      .quantitySelector {
        margin: 0;
      }
    }

    .quantityBasedPricesMobile .quantityBasedPrices {
      padding-top: 10px;
      border-top: 1px solid #E2E2E2;
      text-align: left;
    }

    .mobilePriceHeader {
      background: #F5F7F9 !important;
      display: flex;
      justify-content: space-between;
      padding: 10px !important;
    }

    .mobilePriceBody {
      display: flex;
      justify-content: space-between;
      margin-top: 10px;
    }
    
    .totalPriceWithVat {
      .totalPrice {
        justify-content: space-between;

        p {
          font-size: 14px !important;
          font-weight: 600;
          display: inline;
        }

        .totalPriceSection {
          display: flex;
          justify-content: space-between;
          width: 100%;
          padding-top: 10px;
          border-top: 1px solid #E2E2E2;

          .currentPriceContainer {
            justify-content: flex-end;
          }
        }

        span {
          display: block;
          font-weight: 600;
        }
      }
    }

    td {
      border-top: 1px solid transparent;
      border-bottom: 1px solid transparent;
      background: transparent;
      padding-left: 10px;
      padding-right: 10px;

      &.name,
      &.quantity,
      &.unitPriceNoVat,
      &.totalPriceWithVat,
      &.deleteContainer {
        width: 100%;
      }

      &:first-of-type {
        padding: 10px 10px 0 10px;
      }

      &:last-of-type:not(.totalPriceNoVat) {
        padding: 10px;
      }
    }
  }
`;

const StyledOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 999;
  width: 100%;
  height: 100%;
  background-color: rgba(22, 22, 22, 0.5);

  .container {
    p {
      font-size: 1rem;
      color: ${ props => props.theme.colors.white };
    }
    .buttonsContainer {
      display: flex;
      justify-content: center;
      margin-top: 10px;

      button {
        font-size: 1rem;
        height: 30px;
        width: 60px;
        margin: 0 10px;
      }
    }
  }

  .cds--loading {
    position: absolute;
    top: calc(50% - 15px);
    left: 50%;
    width: 30px;
    height: 30px;
    z-index: 9;
  }
`;

const StyledDeletedOverlay = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 999;
  width: 100%;
  height: calc(100% - 70px);
  background-color: rgba(255, 255, 255, 0.5);

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    height: calc(100% - 160px);
  }
`;

const WrapperTableRow = styled(TableRow)`
  &.promotionContainer {
    td {
      background-color: ${ props => props.theme.products.colors.promotionsRibbon } !important;
    }
    .quantitySelector {
      background-color: transparent;
    }
    .cds--data-table-content {
      border-bottom: 1px solid #B2C2D5;
    }
  }

  &:has(+ .cartPromotionSuggestionsRow) {
    td {
      border-bottom: none;
    }
  }
  
  > td {
    padding: 0;
    position: relative;
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    height: auto !important;
    background: transparent !important;

    &.promotionContainer {
      .cds--data-table-content {
        border-bottom: none;
      }

      td:not(.mobilePriceHeader) {
        background-color: #fff !important;
      }
    }

    &:hover {
      background: #fff !important;
    }

    > td {
      border: none !important;
      background: transparent;

      &:hover {
        background: #fff !important;
        border: none !important;
      }

      tbody {
        background-color: transparent;
      }
    }
  }
`;

const StyledConfirmationContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: #F5F5F5;

  .error {
    font-size: 14px;
    font-weight: 400;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${ props => props.theme.colors.default };
    padding: 0;
    height: 40px;
    width: 200px;
    min-width: 0;
    min-height: 0;
    color: ${ props => props.theme.colors.default };
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    flex-direction: column;
  }
`;

const ProductItem = props => {
  const {
    product,
    orderId,
    ds: dropShipment,
    dsCurrency,
    editable,
    canDelete,
    onChangeQuantity,
    onChangePrice,
    onDelete,
    loading,
    actionsDisabled,
    selectedDSCountry,
    defaultCurrency,
    isExternalReseller,
    expandRowProgrammatically,
    isUnreservable,
    isUnavailable,
  } = props;
  const prodId = product.orderItemId || product.itemId || product.id || product.productId;
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatNumber } = useNumberUtils();
  const preventWheel = useWheelHack();
  const { width } = useScreen();
  const { location } = useRouter();

  const getSource = () => {
    const path = location.pathname;

    if (path === '/checkout/success') return 'success_page';
    if (path === '/checkout') return 'checkout_page';
    if (path.startsWith('/orders/')) return 'order_page';

    return 'unknown';
  };

  const awbs = (product?.orderDetailsAwbs || []).map(awb => awb.awbNumber);
  let maxStockValue;
  if (orderId) {
    maxStockValue = product?.maxQuantityAvailable ? product?.maxQuantityAvailable : 100;
  } else {
    maxStockValue = !product?.stockValue && product?.reservedStockValue ? product?.quantity : product?.stockValue;
  }

  return (
    <StyledTableRow
      // key={ !product.isPromo ? prodId : `${prodId}_${product.quantity}_${product.ronPromoPrice}` }
      key={ prodId }
      theme={ {
        ...theme,
        ds: dropShipment,
        editable,
        updated: product.updated,
        new: product.new,
        promoItem: product.isPromo,
        mainPromoItem: product.mainPromoItem,
        isUnreservable,
        error: (
          !orderId && (
            isUnavailable
            || product.stockValue === 0
            || product.minQuantity > product.stockValue
            || product.quantity > product.stockValue
          )
        )
      } }
      className={ `${product.isPromo ? 'promoItem' : ''} ${isUnreservable ? 'unReservable' : ''}` }
    >
      <TableCell className="name">
        <div>
          { !orderId && (
            <CheckoutBadges isResealed={ !!product.originalCode } isClearance={ product.stockClearingPrice } />
          )}
          <Link
            className="preview"
            to={ `/products/${ product.slug }/pd/${ product.productId || product.id }` }
            state={ {
              component: 'product_order_item',
              source: getSource(),
              clickedOn: 'image',
              productId: product.productId || product.id
            } }
          >
            <Img
              className="image"
              src={ product.pictureUrl }
              unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
              loading="lazy"
              loader={ <Loading withOverlay={ false } small /> }
              alt={ product.title }
            />
          </Link>
          <div className="details">
            <Link
              className="title"
              to={ `/products/${ product.slug }/pd/${ product.productId || product.id }` }
              state={ {
                component: 'product_order_item',
                source: getSource(),
                clickedOn: 'title',
                productId: product.productId || product.id
              } }
            >
              { product.name || product.title }
            </Link>
            { width > 767 && (
              <>
                <p>{ t('products.code') }: <span>{ product.code }</span></p>
                { !orderId && renderStock({ t, product: { ...product, hasPrice: true } }) }
                { (!orderId && !!product?.priceScales?.length) && (
                  <QuantityBasedPrice
                    className="quantityBasedPrices"
                    priceScales={ product.priceScales }
                    visibleItems={ 0 }
                    currency={ defaultCurrency }
                  />
                )}
                { awbs.length > 0 && (
                  <div className="productAWBs">
                    <span>{ t('orders.awbs') }:</span>
                    { awbs.map(awb => (
                      <button key={ awb } type="button" onClick={ () => expandRowProgrammatically(awb) }>{ awb }</button>
                    )) }
                  </div>
                ) }
              </>
            )}
          </div>
        </div>
      </TableCell>
      { (width <= 767 && editable && canDelete && !product.isPromo) && (
        <TableCell className="mobileDelete">
          <button
            type="button"
            disabled={ loading || actionsDisabled || product.updated || product.new }
            onClick={ onDelete }
            data-test="onDeleteOrderItem"
            data-testid={ product.productId }
          >
            <X /> { t('common.delete') }
          </button>
        </TableCell>
      ) }
      <TableCell className={ `quantity ${ !editable ? 'immutable' : '' }` }>
        { width <= 767 && (
          <div className="mobileDetails">
            <p>{ t('products.code') }: <span>{ product.code }</span></p>
            { !editable && <p>{ t('products.quantity') }: <span>{ product.quantity }</span></p>}
            { !orderId && renderStock({ t, product: { ...product, hasPrice: true } }) }
            { awbs.length > 0 && (
              <div className="productAWBs">
                <span>{ t('orders.awbs') }:</span>
                { awbs.map(awb => (
                  <button key={ awb } type="button" onClick={ () => expandRowProgrammatically(awb) }>{ awb }</button>
                )) }
              </div>
            ) }
          </div>
        )}
        { (orderId || !Object.keys(product?.warnings || {}).length) && (
          <QuantitySelect
            size="lg"
            disabled={ !editable || product.isPromo || product.new }
            value={ product.quantity }
            min={ Math.max(product.minQuantity, 1) }
            step={ Math.max(product.minQuantity, 1) }
            max={ maxStockValue }
            onChange={ onChangeQuantity }
            data-test="orderItemChangeQty"
            data-testid={ product.id }
          />
        ) }
        { (editable && !orderId) && (
          <>
            { (product?.quantity > product?.stockValue)
              ? (
                <div>
                  <p>
                    { t('product.notEnoughStock', {
                      qty: product.quantity,
                      stock: product.stockValue
                    }) }
                  </p>
                </div>
              )
              : null
            }
          </>
        ) }
        {/* { editable && (
          <div>
            { (product?.quantity > product?.maxQuantityAvailable)
              ? (
                <p>
                  { t('product.notEnoughStock', {
                    qty: product.quantity,
                    stock: product.stockValue
                  }) }
                </p>
              )
              : null
            }
          </div>
        ) } */}
        { (editable && orderId && product.maxQuantityAvailable && !product.isPromo && !isUnreservable) && (
          <div className="insufficient">
            <p>
              <AlertTriangle />
              { t('order.insufficientQuantity', { qty: product.maxQuantityAvailable }) }
            </p>
          </div>
        ) }
        { (editable && orderId && product.originalQuantity && !product.maxQuantityAvailable && !product.isPromo) && (
          <div className="sufficient">
            <p>
              <Check />
              { t('order.sufficientQuantity') }
            </p>
          </div>
        ) }
      </TableCell>
      { (width <= 767 && !orderId && !!product?.priceScales?.length) && (
        <TableCell className="quantityBasedPricesMobile">
          <QuantityBasedPrice
            className="quantityBasedPrices"
            priceScales={ product.priceScales }
            visibleItems={ 0 }
            currency={ defaultCurrency }
          />
        </TableCell>
      )}
      { width > 767 ? (
        <>
          <TableCell className="unitPriceNoVat">
            { (orderId || !Object.keys(product?.warnings || {}).length) && (
              <Price
                price={ product.price }
                promoPrice={ product.promoPrice }
                currency={ defaultCurrency }
                withTooltip={ 'greenStampValue' in product }
                tooltipContent={ `
                  ${ t('products.greenStamp') }
                    ${ formatNumber(product.greenStampValue, { currency: defaultCurrency }) }
                  ` }
              />
            ) }
          </TableCell>
          <TableCell className="totalPriceNoVat">
            <div>
              { (orderId || !Object.keys(product?.warnings || {}).length) && (
                <>
                  { editable
                    ? (
                      <Price
                        currency={ defaultCurrency }
                        price={ product?.price * product?.quantity }
                        promoPrice={ product?.promoPrice * product?.quantity }
                      />
                    ) : (
                      <Price
                        currency={ defaultCurrency }
                        price={ product?.totalPrice }
                        promoPrice={ orderId ? product?.totalPromoPrice : product?.totalPrice }
                        withTooltip={ 'greenStampValue' in product }
                        tooltipContent={ `
                          ${ t('products.greenStamp') }
                            ${ formatNumber(product.quantity * product.greenStampValue, { currency: defaultCurrency }) }
                          ` }
                      />
                    )}
                </>
              ) }
            </div>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell className="mobilePriceHeader">
            <span>
              { isExternalReseller ? t('product.unitPrice') : t('product.unitPriceNoVat') }
            </span>
            <span>
              { isExternalReseller ? t('product.totalPrice') : t('product.totalPriceNoVat') }
            </span>
          </TableCell>
          <TableCell className="mobilePriceBody">
            { (orderId || !Object.keys(product?.warnings || {}).length) && (
              <span>
                <Price
                  price={ product.price }
                  promoPrice={ product.promoPrice }
                  currency={ defaultCurrency }
                  withTooltip={ 'greenStampValue' in product }
                  tooltipContent={ `
                    ${ t('products.greenStamp') }
                      ${ formatNumber(product.greenStampValue, { currency: defaultCurrency }) }
                    ` }
                />
              </span>
            ) }
            { (orderId || !Object.keys(product?.warnings || {}).length) && (
              <span>
                { editable
                  ? (
                    <Price
                      currency={ defaultCurrency }
                      price={ product?.price * product?.quantity }
                      promoPrice={ product?.promoPrice * product?.quantity }
                    />
                  ) : (
                    <Price
                      currency={ defaultCurrency }
                      price={ product?.totalPrice }
                      promoPrice={ orderId ? product?.totalPromoPrice : product?.totalPrice }
                      withTooltip={ 'greenStampValue' in product }
                      tooltipContent={ `
                        ${ t('products.greenStamp') }
                          ${ formatNumber(product.quantity * product.greenStampValue, { currency: defaultCurrency }) }
                        ` }
                    />
                  )}
              </span>
            ) }
          </TableCell>
        </>
      ) }
      { dropShipment && (
        <TableCell className="totalPriceWithVat">
          <div className="totalPrice">
            { editable
              ? (
                <>
                  <span>{ t('product.priceWithVatForFinalClient') }</span>
                  <div>
                    <TextInput
                      type="number"
                      labelText=""
                      id={ uniqueId(`${ product.id }_priceWithVat`) }
                      tabIndex={ 0 }
                      min={ 0 }
                      step="any"
                      aria-label={ t('product.priceWithVatForFinalClient') }
                      required
                      value={ 'cashOnDeliveryValue' in product
                        ? product.cashOnDeliveryValue
                        : ''
                      }
                      onChange={ e => onChangePrice(product, e.target.value) }
                      ref={ preventWheel }
                    />
                    <p>{ COUNTRIES[selectedDSCountry]?.symbol }</p>
                  </div>
                </>
              )
              : (
                <div className="totalPriceSection">
                  <p>{ t('product.priceWithVatForFinalClient') }:</p>
                  <Price
                    price={ dropShipment ? product.cashOnDeliveryValue : product.totalPrice }
                    promoPrice={ product.cashOnDeliveryValue }
                    currency={ dsCurrency || defaultCurrency }
                  />
                </div>
              )
            }
          </div>
        </TableCell>
      ) }
      { (width > 767 && editable && canDelete) && (
        <TableCell className="deleteContainer">
          { !product.isPromo && (
            <Button
              className="delete"
              onClick={ onDelete }
              disabled={ loading || actionsDisabled || product.updated || product.new }
              data-test="onDeleteOrderItem"
              data-testid={ product.productId }
            >
              <Trash size={ 18 } />
            </Button>
          ) }
        </TableCell>
      ) }
    </StyledTableRow>
  );
};

ProductItem.propTypes = {
  product: object,
  ds: bool,
  dsCurrency: string,
  actionsDisabled: bool,
  orderId: string,
  editable: bool,
  canDelete: bool,
  loading: bool,
  selectedDSCountry: number,
  onChangeQuantity: func,
  onChangePrice: func,
  onDelete: func,
  defaultCurrency: string,
  isExternalReseller: bool,
  expandRowProgrammatically: func,
  isUnreservable: bool,
  isUnavailable: bool,
};

const ProductOrderItem = ({
  dropShipment,
  dsCurrency,
  orderId,
  product: prod,
  onCancelChanges,
  editable,
  canDelete,
  onChangeQuantity,
  onChangePrice,
  selectedDSCountry,
  onDelete,
  actionsDisabled,
  loading,
  error,
  defaultCurrency,
  isExternalReseller,
  expandRowProgrammatically,
  unreservableProducts = [],
  unavailableProductIds = [],
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isUnreservable = unreservableProducts.includes(prod.code);
  const isUnavailable = unavailableProductIds.includes(String(prod.productId || prod.id));
  const hasAvailablePromotions = Array.isArray(prod?.availablePromotions) && prod.availablePromotions.length > 0;

  let colSpan = dropShipment ? 5 : 4;
  if (editable) {
    colSpan += 1;
  }
  let { promotion } = prod;
  if (
    !promotion
    && prod.appliedPromotions
  ) {
    const appliedPromo = prod.appliedPromotions?.at(0) || prod.promotion;
    if (typeof appliedPromo === 'string') {
      promotion = {
        promotionId: appliedPromo
      };
    } else if (appliedPromo?.promotionId) {
      promotion = appliedPromo;
    }
  }

  if (promotion?.promotionId) {
    let promoItems = prod.promoItems[promotion.promotionId];
    if (
      promoItems
      && !Array.isArray(promoItems)
    ) {
      promoItems = [promoItems];
    }
    return (
      <>
        <StyledTableRow theme={ theme } className="promotionHeading">
          <TableCell colSpan={ colSpan }>
            <div className="promoHeading">
              <Boxes />
              <p
                dangerouslySetInnerHTML={ {
                  __html: t(`products.promotionData.type.${ promotion.type }`)
                } }
              />
            </div>
          </TableCell>
        </StyledTableRow>
        <WrapperTableRow className="promotionContainer" theme={ theme }>
          <TableCell colSpan={ colSpan }>
            { prod.deleted && orderId && <StyledDeletedOverlay /> }
            { loading && (
              <StyledOverlay theme={ theme }>
                { loading && <Loading small withOverlay={ false } active /> }
              </StyledOverlay>
            ) }
            <Table>
              <tbody>
                <ProductItem
                  orderId={ orderId }
                  product={{ ...prod, mainPromoItem: true }}
                  actionsDisabled={ actionsDisabled }
                  canDelete={ canDelete }
                  ds={ dropShipment }
                  editable={ editable }
                  onChangeQuantity={ onChangeQuantity }
                  onChangePrice={ onChangePrice }
                  onDelete={ onDelete }
                  loading={ loading }
                  selectedDSCountry={ selectedDSCountry }
                  dsCurrency={ dsCurrency }
                  defaultCurrency={ defaultCurrency }
                  isExternalReseller={ isExternalReseller }
                  expandRowProgrammatically={ expandRowProgrammatically }
                  isUnavailable={ isUnavailable }
                />
                { (promoItems || []).map(p => (
                  <ProductItem
                    orderId={ orderId }
                    actionsDisabled={ actionsDisabled }
                    key={ p.orderDetailId || p.itemId || uniqueId(p.id || p.productId) }
                    product={{ ...p, isPromo: true }}
                    canDelete={ canDelete }
                    ds={ dropShipment }
                    editable={ editable }
                    onChangeQuantity={ onChangeQuantity }
                    onChangePrice={ onChangePrice }
                    onDelete={ onDelete }
                    loading={ loading }
                    selectedDSCountry={ selectedDSCountry }
                    dsCurrency={ dsCurrency }
                    defaultCurrency={ defaultCurrency }
                    isExternalReseller={ isExternalReseller }
                  />
                )) }
                { !!error && (
                  <StyledTableRow theme={ { ...theme, error: true } } className="error">
                    <TableCell colSpan={ colSpan }>
                      { error }
                    </TableCell>
                  </StyledTableRow>
                ) }
                { (prod.updated || prod.new || prod.deleted) && orderId && (
                  <StyledTableRow
                    theme={ {
                      ...theme,
                      updated: prod.updated,
                      new: prod.new,
                      error: (
                        prod.stockValue === 0
                        || prod.minQuantity > prod.stockValue
                      )
                    } }
                  >
                    <TableCell colSpan={ colSpan }>
                      <StyledConfirmationContainer theme={ theme }>
                        <span><AlertTriangle size={ 24 } /></span>
                        { prod.new && <p className="error">{ t('order.toBeAdded') }</p> }
                        { prod.deleted && <p className="error">{ t('order.toBeDeleted') }</p> }
                        { prod.updated && <p className="error">{ t('order.toBeUpdated') }</p> }
                        <Button
                          kind="ghost"
                          className="cancel"
                          data-test="onCancelChanges"
                          onClick={ onCancelChanges }
                          disabled={ loading }
                        >
                          { t('order.cancelChanges') }
                        </Button>
                      </StyledConfirmationContainer>
                    </TableCell>
                  </StyledTableRow>
                ) }
                <TableRow className="rowBorder">
                  <TableCell colSpan={ colSpan }>
                    <hr />
                  </TableCell>
                </TableRow>
              </tbody>
            </Table>
          </TableCell>
        </WrapperTableRow>
        { (!orderId && hasAvailablePromotions) && (
          <CartPromotionSuggestions
            colSpan={ colSpan }
            product={ prod }
            theme={ theme }
            currency={ isExternalReseller ? defaultCurrency : CURRENCY_RON }
          />
        ) }
      </>
    );
  }

  return (
    <>
      <WrapperTableRow>
        <TableCell colSpan={ colSpan }>
          { prod.deleted && orderId && <StyledDeletedOverlay /> }
          { loading && (
            <StyledOverlay theme={ theme }>
              { loading && <Loading small withOverlay={ false } active /> }
            </StyledOverlay>
          )}
          <Table>
            <tbody>
              <ProductItem
                orderId={ orderId }
                product={ prod }
                canDelete={ canDelete }
                actionsDisabled={ actionsDisabled }
                ds={ dropShipment }
                editable={ editable }
                onChangeQuantity={ onChangeQuantity }
                onChangePrice={ onChangePrice }
                onDelete={ onDelete }
                loading={ loading }
                selectedDSCountry={ selectedDSCountry }
                dsCurrency={ dsCurrency }
                defaultCurrency={ defaultCurrency }
                isExternalReseller={ isExternalReseller }
                expandRowProgrammatically={ expandRowProgrammatically }
                isUnreservable={ isUnreservable }
                isUnavailable={ isUnavailable }
              />
              { !!error && (
                <StyledTableRow theme={ { ...theme, error: true } } className="error">
                  <TableCell colSpan={ colSpan }>
                    { error }
                  </TableCell>
                </StyledTableRow>
              ) }
              { isUnreservable && (
                <StyledTableRow className="isUnreservable">
                  <TableCell colSpan={ colSpan }>
                    <StyledConfirmationContainer theme={ theme }>
                      <AlertTriangle />
                      <p className="error">{ t('order.isUnreservable') }</p>
                    </StyledConfirmationContainer>
                  </TableCell>
                </StyledTableRow>
              ) }
              { (prod.updated || prod.new || prod.deleted) && orderId && (
                <StyledTableRow
                  theme={ {
                    ...theme,
                    updated: prod.updated,
                    new: prod.new,
                    error: (
                      prod.stockValue === 0
                      || prod.minQuantity > prod.stockValue
                    )
                  } }
                >
                  <TableCell colSpan={ colSpan }>
                    <StyledConfirmationContainer theme={ theme }>
                      <span><AlertTriangle size={ 24 } /></span>
                      { prod.new && <p className="error">{ t('order.toBeAdded') }</p> }
                      { prod.deleted && <p className="error">{ t('order.toBeDeleted') }</p> }
                      { prod.updated && <p className="error">{ t('order.toBeUpdated') }</p> }
                      <Button
                        kind="ghost"
                        className="cancel"
                        data-test="onCancelChanges"
                        onClick={ onCancelChanges }
                        disabled={ loading }
                      >
                        { t('order.cancelChanges') }
                      </Button>
                    </StyledConfirmationContainer>
                  </TableCell>
                </StyledTableRow>
              ) }
              <TableRow className="rowBorder">
                <TableCell colSpan={ colSpan }>
                  <hr />
                </TableCell>
              </TableRow>
            </tbody>
          </Table>
        </TableCell>
      </WrapperTableRow>
      { (!orderId && hasAvailablePromotions) && (
        <CartPromotionSuggestions
          colSpan={ colSpan }
          product={ prod }
          theme={ theme }
          currency={ isExternalReseller ? defaultCurrency : CURRENCY_RON }
        />
      ) }
    </>
  );
};

ProductOrderItem.propTypes = {
  product: ProductPropType,
  actionsDisabled: bool,
  dropShipment: bool,
  dsCurrency: string,
  editable: bool,
  onDelete: func,
  error: string,
  orderId: string,
  canDelete: bool,
  loading: bool,
  selectedDSCountry: number,
  onCancelChanges: func,
  onChangePrice: func,
  onChangeQuantity: func,
  defaultCurrency: string,
  isExternalReseller: bool,
  expandRowProgrammatically: func,
  unreservableProducts: array,
  unavailableProductIds: array,
};

ProductOrderItem.displayName = 'ProductOrderItemComponent';

export default ProductOrderItem;
